import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, ChipSelector } from "@repo/ui";
import AsideComponent from "../../../components/AsideComponent/AsideComponent";
import filterIcon from "../../../assets/icons/filter.svg";
import { getFilters } from "../../../services/productApiService";
import ToggleSwitch from "../../../components/ToggleSwitch/ToggleSwitch";
import DualRangeSlider from "../../../components/DualRangeSlider/DualRangeSlider";
import { withTranslations } from "../../../hoc/withTranslations/withTranslations";

const LOCALE = "modules_MyGiftTable_components_FiltersComponent";

interface FiltersComponentProps {
  onApplyFilters: (filters: Filters) => void;
  initialCategory?: string | null;
}

export interface Filters {
  category?: string;
  category_id?: string;
  brand?: string;
  recommended?: boolean;
  exclusive?: boolean;
  tags?: string;
  price?: { min: number; max: number };
  sort?: { field: string; order: "asc" | "desc" };
}

const FiltersComponent: React.FC<FiltersComponentProps> = ({
  onApplyFilters,
  initialCategory = null,
}) => {
  const { t } = useTranslation(LOCALE);
  const [isAsideVisible, setIsAsideVisible] = useState(false);
  const [category, setCategory] = useState<string | undefined>(
    initialCategory || ""
  );
  const [brand, setBrand] = useState<string | undefined>();
  const [recommended, setRecommended] = useState<boolean | undefined>(
    undefined
  );
  const [exclusive, setExclusive] = useState<boolean | undefined>(undefined);
  const [tags, setTags] = useState<string | undefined>();
  const [price, setPrice] = useState<{ min: number; max: number } | undefined>(
    undefined
  );
  const [sort, setSort] = useState<{ field: string; order: "asc" | "desc" }>();
  const [categories, setCategories] = useState<string[]>([]);
  const [categoriesMap, setCategoriesMap] = useState<Map<string, string>>(
    new Map()
  );
  const [brands, setBrands] = useState<string[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const filterData = await getFilters();
        const sortedCategories = [...new Set(filterData.categories)].sort();
        const sortedBrands = [...new Set(filterData.brands)].sort();

        const categoryToIdMap = new Map<string, string>();
        filterData.categories.forEach((category, index) => {
          categoryToIdMap.set(category, filterData.categoryIds[index]);
        });
        setCategoriesMap(categoryToIdMap);

        setCategories(sortedCategories);
        setBrands(sortedBrands);

        if (filterData.tags && filterData.tags.length > 0) {
          setAvailableTags(filterData.tags);
        }
      } catch (error) {
        console.error(t("errorFetchingFilters"), error);
      }
    };

    fetchFilters();
  }, []);

  const handleApplyFilters = () => {
    onApplyFilters({
      category,
      category_id: categoriesMap.get(category as string),
      brand,
      recommended,
      exclusive,
      tags,
      price,
      sort,
    });
    setIsAsideVisible(false);
  };

  const handleRangeChange = (values: { min: number; max: number }) => {
    setPrice(values);
  };

  const formatCurrency = (value: number) => {
    return `$${value.toLocaleString()}`;
  };

  const handleSortChange = (id: string | null) => {
    if (id === "high-to-low") {
      setSort({ field: "price", order: "desc" });
    } else if (id === "low-to-high") {
      setSort({ field: "price", order: "asc" });
    } else {
      setSort(undefined);
    }
  };

  return (
    <>
      <div className="md:hidden">
        <button
          onClick={() => setIsAsideVisible(true)}
          className="p-2 border rounded flex items-center justify-center"
        >
          <img src={filterIcon} alt={t("filters")} className="h-6 w-6" />
        </button>
      </div>
      <div className="hidden md:block">
        <Button
          label={
            <div className="flex items-center justify-between">
              <span>{t("filters")}</span>
              <img
                src={filterIcon}
                alt={t("filters")}
                className="ml-2 h-4 w-4"
              />
            </div>
          }
          type="secondary"
          onClick={() => setIsAsideVisible(true)}
        />
      </div>
      <AsideComponent
        title={t("filters")}
        isVisible={isAsideVisible}
        onClose={() => setIsAsideVisible(false)}
      >
        <div
          className="space-y-8 max-h-[calc(100vh-150px)] overflow-y-auto px-4"
          style={{ paddingBottom: "1rem" }} // Ensures space for the apply button
        >
          <div>
            <label className="block mb-2">{t("categories")}</label>
            <select
              className="w-full p-2 border"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              disabled={!!initialCategory}
            >
              <option value="">{t("allCategories")}</option>
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block mb-2">{t("brands")}</label>
            <select
              className="w-full p-2 border"
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
            >
              <option value="">{t("allBrands")}</option>
              {brands.map((brand) => (
                <option key={brand} value={brand}>
                  {brand}
                </option>
              ))}
            </select>
          </div>

          {availableTags.length > 0 && (
            <div>
              <label className="block mb-2">{t("tags")}</label>
              <select
                className="w-full p-2 border"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
              >
                <option value="">{t("selectTag")}</option>
                {availableTags.map((tag) => (
                  <option key={tag} value={tag}>
                    {tag}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex items-center justify-between">
            <label className="block">{t("recommended")}</label>
            <ToggleSwitch checked={recommended} onChange={setRecommended} />
          </div>

          <div className="flex items-center justify-between">
            <label className="block">{t("exclusive")}</label>
            <ToggleSwitch checked={exclusive} onChange={setExclusive} />
          </div>

          <div>
            <label className="block mb-4">{t("price")}</label>
            <DualRangeSlider
              min={1}
              max={100000}
              step={1}
              onChange={handleRangeChange}
              formatValue={formatCurrency}
            />
          </div>

          <div>
            <label className="block mb-2">{t("sortBy")}</label>
            <ChipSelector
              items={[
                { id: "relevant", label: t("relevant") },
                { id: "high-to-low", label: t("highToLow") },
                { id: "low-to-high", label: t("lowToHigh") },
              ]}
              selected={
                !sort
                  ? "relevant"
                  : sort.order === "desc"
                    ? "high-to-low"
                    : "low-to-high"
              }
              onChange={handleSortChange}
            />
          </div>
        </div>

        <div className="absolute bottom-4 left-0 w-full flex justify-center px-4">
          <Button label={t("applyFilters")} onClick={handleApplyFilters} />
        </div>
      </AsideComponent>
    </>
  );
};

export default withTranslations(LOCALE)(FiltersComponent);
